import { apiGet, apiPost, type PageResult } from '~/utils/api'

/**
 * Product reviews API (customer-facing).
 *
 * Reviews are attached to the device model (trade-in product id), so all
 * colour/storage variants of the same model share reviews. Only approved
 * reviews are returned here; submission requires a verified purchase and is
 * held for moderation before appearing.
 */
export interface ProductReview {
  id: string
  rating: number
  title: string | null
  body: string | null
  authorName: string | null
  createdAt: string
}

export interface ProductRatingSummary {
  /** Average of approved reviews (1 decimal); null when there are none. */
  average: number | null
  count: number
}

export interface CreateReviewPayload {
  productId: string
  rating: number
  title?: string
  body?: string
}

/** Whether the current (logged-in) user may review this model. */
export interface ReviewEligibility {
  /** Has a paid purchase of this model. */
  purchased: boolean
  /** Already left a review for this model (one per user/model). */
  alreadyReviewed: boolean
  /** Muted by an admin (can_review = false). */
  blocked: boolean
  /** purchased && !alreadyReviewed && !blocked. */
  canReview: boolean
}

export function useReviewsAPI() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  function fetchSummary(productId: string): Promise<ProductRatingSummary> {
    return apiGet<ProductRatingSummary>(`/api/app/v1/reviews/products/${productId}/summary`)
  }

  function fetchReviews(productId: string, page = 0, size = 10): Promise<PageResult<ProductReview>> {
    return apiGet<PageResult<ProductReview>>(
      `/api/app/v1/reviews/products/${productId}?page=${page}&size=${size}`,
    )
  }

  /** Requires login (authenticated endpoint); only call when logged in. */
  function fetchEligibility(productId: string): Promise<ReviewEligibility> {
    return apiGet<ReviewEligibility>(`/api/app/v1/reviews/eligibility/${productId}`)
  }

  async function submitReview(payload: CreateReviewPayload): Promise<ProductReview> {
    loading.value = true
    error.value = null
    try {
      return await apiPost<ProductReview>('/api/app/v1/reviews', payload)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Unknown error'
      throw e
    } finally {
      loading.value = false
    }
  }

  return { loading, error, fetchSummary, fetchReviews, fetchEligibility, submitReview }
}
